import { Badge, Box, Code, Flex, Heading, Text } from '@workos-inc/design-system';
import * as React from 'react';
import { ReferenceEntity, ReferenceLanguage } from 'src/utils/reference-schema';
import { HttpMethodBadge } from './http-method-badge';
import { Link } from './link';
import { ReferenceAccordion } from './reference-accordion';
import { useReferenceData } from './reference-data-provider';
import { TypeDefinitionContext } from './type-definition-context';
import { getSyntax } from './type-definition-entity';
import { TypeDefinitionToken } from './type-definition-token';
interface ReferenceDefinitionProps {
  language: ReferenceLanguage;
  referenceId: string;
}
const ReferenceDefinition = ({
  language,
  referenceId
}: ReferenceDefinitionProps) => {
  const reference = useReferenceData(language);
  const curlReference = useReferenceData('curl');

  // Fall back to curl when the entity isn't defined for the current language.
  // The curl reference is always auto-generated, so this ensures the right
  // panel shows REST API details regardless of the selected SDK language.
  const languageEntity = reference?.find(({
    id
  }) => id && id === referenceId);
  const curlEntity = curlReference?.find(({
    id
  }) => id && id === referenceId);
  const entity = languageEntity ?? curlEntity;
  const resolvedLanguage = languageEntity ? language : 'curl';
  if (!entity) {
    return null;
  }
  const showHttpMethodBadge = resolvedLanguage === 'curl' && entity.type;
  return <TypeDefinitionContext.Provider value={{
    language: resolvedLanguage,
    entity
  }} data-sentry-element="TypeDefinitionContext.Provider" data-sentry-component="ReferenceDefinition" data-sentry-source-file="reference-definition.tsx">
      <Text asChild size="2" data-sentry-element="Text" data-sentry-source-file="reference-definition.tsx">
        <Flex key={language} data-algolia-exclude direction="column" gap="3" data-sentry-element="Flex" data-sentry-source-file="reference-definition.tsx">
          {(entity.title || entity.properties) && <Flex direction="column" gap="2">
              {entity.title && <Flex direction="column" gap="2">
                  {(entity.sendsEmail || entity.featureFlag) && <Flex align="center" gap="2" wrap="wrap">
                      {entity.sendsEmail && <Badge>Sends email</Badge>}
                      {entity.featureFlag && <Badge>Feature flagged</Badge>}
                    </Flex>}

                  <Flex align="start" gap="2" wrap="wrap">
                    {showHttpMethodBadge &&
              // Introducing lint rule banning type assertions
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              <HttpMethodBadge method={entity.type as string}>
                        <Code variant="ghost">{entity.type}</Code>
                      </HttpMethodBadge>}

                    <Heading as="h3" size="2" style={{
                whiteSpace: 'pre-line'
              }}>
                      <Code variant="ghost">
                        {insertWordBreakOpportunities(entity.title)}
                      </Code>
                    </Heading>
                  </Flex>
                </Flex>}

              {entity.properties && <ReferenceAccordion entities={entity.properties} syntax={getSyntax(entity)} />}
            </Flex>}

          {entity.parameters && <Parameters parameters={entity.parameters} />}
          {entity.returns && <Returns values={entity.returns} />}
        </Flex>
      </Text>
    </TypeDefinitionContext.Provider>;
};
interface ParametersProps {
  parameters: ReferenceEntity[];
}
const Parameters = ({
  parameters
}: ParametersProps) => {
  const firstParam = parameters[0];
  const unwrap = typeof firstParam === 'object' && firstParam.unwrap;
  return <Box data-sentry-element="Box" data-sentry-component="Parameters" data-sentry-source-file="reference-definition.tsx">
      <Flex align="center" height="24px" data-sentry-element="Flex" data-sentry-source-file="reference-definition.tsx">
        <Heading as="h4" size="2" data-sentry-element="Heading" data-sentry-source-file="reference-definition.tsx">
          <code className="ReferenceDefinitionCode">Parameters </code>
          <code className="ReferenceDefinitionCode" style={{
          fontWeight: 400
        }}>
            {unwrap && firstParam.type && <TypeDefinitionToken token={firstParam.type} />}
          </code>
        </Heading>
      </Flex>

      <ReferenceAccordion entities={unwrap ? firstParam.properties : parameters} syntax={unwrap ? 'object' : 'list'} data-sentry-element="ReferenceAccordion" data-sentry-source-file="reference-definition.tsx" />
    </Box>;
};
interface ReturnsProps {
  // There may be multiple return values in some programming languages, e.g. in Go
  values?: ReferenceEntity[];
}
const Returns = ({
  values = []
}: ReturnsProps) => {
  // If the only return value is an entity that we need to unwrap
  const unwrapped = values.length === 1 && values[0]?.unwrap && values[0];

  // Separate entities with links from entities that go into a single accordion.
  // `ReferenceEntity` will be rendered as a single link to the entity.
  // `ReferenceEntity[]` will form an accordion of `ReferenceEntity` items.
  const grouped: (ReferenceEntity | ReferenceEntity[])[] = [];
  if (!unwrapped) {
    for (const entity of values) {
      const lastItem = grouped[grouped.length - 1];
      if (entity.url) {
        grouped.push(entity);
      } else if (Array.isArray(lastItem)) {
        lastItem.push(entity);
      } else {
        grouped.push([entity]);
      }
    }
  }
  return <Box data-sentry-element="Box" data-sentry-component="Returns" data-sentry-source-file="reference-definition.tsx">
      <Flex align="center" height="24px" data-sentry-element="Flex" data-sentry-source-file="reference-definition.tsx">
        <Heading as="h4" size="2" data-sentry-element="Heading" data-sentry-source-file="reference-definition.tsx">
          <code className="ReferenceDefinitionCode">Returns </code>

          {unwrapped && <code className="ReferenceDefinitionCode" style={{
          fontWeight: 400
        }}>
              <TypeDefinitionToken token={unwrapped.type} />
            </code>}
        </Heading>
      </Flex>

      {unwrapped && <ReferenceAccordion entities={unwrapped.properties} syntax={getSyntax(unwrapped)} />}

      {grouped.map((value, i) => {
      if (Array.isArray(value)) {
        return <ReferenceAccordion key={i} entities={value} syntax="list" />;
      }
      return <Flex key={value.key} gap="2">
            {values.length > 1 && <Flex justify="center" width="8px">
                •
              </Flex>}
            <Link shallow href={value.url}>
              <code className="ReferenceDefinitionCode">{value.key}</code>
            </Link>
          </Flex>;
    })}
    </Box>;
};
interface WithReferenceDefinitionProps {
  children?: React.ReactNode;
  language?: ReferenceLanguage;
  referenceId?: string;
}
export const WithReferenceDefinition = ({
  children,
  language,
  referenceId
}: WithReferenceDefinitionProps) => {
  if (!referenceId || !language) {
    return <>{children}</>;
  }
  return <div className="ReferenceDefinitionLayout" data-sentry-component="WithReferenceDefinition" data-sentry-source-file="reference-definition.tsx">
      <div className="ReferenceDefinitionLayoutLeft">{children}</div>
      <div className="ReferenceDefinitionLayoutRight">
        <div className="ReferenceDefinitionContent">
          <ReferenceDefinition language={language} referenceId={referenceId} data-sentry-element="ReferenceDefinition" data-sentry-source-file="reference-definition.tsx" />
        </div>
      </div>
    </div>;
};

// Convert OpenAPI-style path params `{param}` to Express-style `:param`
const convertPathParams = (str: string) => str.replace(/\{([^}]+)\}/g, ':$1');

// We'll insert word break opportunities before ".", "/", "\", "->"
const insertWordBreakOpportunities = (str: string) => convertPathParams(str).split(/(\.[^.]+)|(\/[^\/]+)|(\\[^\\]+)|(->[^->]+)/)
// Remove empty strings and undefined values
.filter(Boolean).map((fragment, i, arr) => <React.Fragment key={i}>
        {nowrapPhpPropertyAccess(fragment)}
        {i + 1 !== arr.length && <wbr />}
      </React.Fragment>);

// Hyphens are a natural word-break opportunity for the browser; we don't want that.
const nowrapPhpPropertyAccess = (str: string) => str.split(/(->)/).map((part, i) => part === '->' ? <span key={i} style={{
  whiteSpace: 'nowrap'
}}>
        {part}
      </span> : part);